'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden, .reveal-hidden-left, .reveal-hidden-right').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" className="py-28 relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 mandala-bg pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5">
        <div className="grid lg:grid-cols-2 gap-20 items-center">

          {/* Left — Bento image collage */}
          <div className="grid grid-cols-2 gap-5 reveal-hidden-left">
            <div className="space-y-5">
              <div className="rounded-4xl overflow-hidden aspect-[3/4] shadow-warm-lg">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_17c46743f-1768656184958.png"
                  alt="Garba instructor demonstrating traditional dance mudra hand gestures"
                  className="w-full h-full object-cover" />
                
              </div>
              <div className="bg-primary rounded-4xl p-7 text-white">
                <h3 className="font-display text-3xl italic mb-2">Pure.</h3>
                <p className="text-sm font-medium opacity-90">Authentic Gujarati Garba tradition, preserved & celebrated.</p>
              </div>
            </div>
            <div className="pt-16 space-y-5">
              <div className="glass rounded-4xl p-7 shadow-warm-md">
                <p className="font-display text-4xl font-bold text-primary mb-1">98%</p>
                <p className="text-xs uppercase tracking-widest text-muted font-semibold">Students return each year</p>
              </div>
              <div className="rounded-4xl overflow-hidden aspect-[3/4] shadow-warm-lg">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1dc0bd021-1768204919057.png"
                  alt="Group of students in colorful chaniya choli performing synchronized Garba"
                  className="w-full h-full object-cover" />
                
              </div>
            </div>
          </div>

          {/* Right — Content */}
          <div className="space-y-8 reveal-hidden-right">
            <div>
              <p className="text-primary font-bold uppercase tracking-[0.3em] text-[11px] mb-3">Our Story</p>
              <h2 className="font-display text-5xl md:text-6xl font-black leading-tight text-foreground">
                Where Culture
                <br />
                <span className="font-display italic text-gradient-warm">Comes Alive.</span>
              </h2>
            </div>
            <p className="text-[17px] text-muted leading-relaxed">
              Founded in 2008 by Guru Ravi Sharma, our group has been the heartbeat of Ahmedabad&apos;s Navratri celebrations. 
              We train students in authentic Raas-Garba, folk dance, and dandiya with deep respect for our Gujarati heritage.
            </p>

            <div className="space-y-5">
              {[
              {
                icon: '🎓',
                title: 'Gurukul-Style Learning',
                desc: 'Small batches of 15–20 students ensure personal attention and rapid skill development.'
              },
              {
                icon: '🏆',
                title: 'Competition Champions',
                desc: 'Our students have won 40+ state and national Garba competitions since 2010.'
              }]?.
              map((item) =>
              <div key={item?.title} className="flex gap-5 items-start group">
                  <div className="w-14 h-14 shrink-0 rounded-2xl bg-primary/8 border border-primary/15 flex items-center justify-center text-2xl group-hover:bg-primary group-hover:text-white transition-all">
                    {item?.icon}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-foreground mb-1">{item?.title}</h4>
                    <p className="text-muted text-sm leading-relaxed">{item?.desc}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}